import { msg } from "@lingui/core/macro";
import {
  createContext,
  createEffect,
  createMemo,
  type JSX,
  onMount,
  type ParentComponent,
  untrack,
  useContext,
} from "solid-js";
import { fromActorRef, useActorRef } from "@fleet/state-machines/solid";
import {
  type AbstractPowerSyncDatabase,
  appBootMachine,
  createProfileHandlers,
  isSignOutInProgress,
  queryKeys,
  type Session,
  signOutMachine,
} from "@gymlog/shared";
import { useLocation, useNavigate } from "@tanstack/solid-router";
import { usePowerSyncContext } from "./power-sync-context";
import { useI18n } from "./i18n-provider";
import { useExecuteSignOut, useSession } from "@/lib/auth";
import { api, fetchApi } from "@/lib/api";
import { queryClient } from "@/lib/query-client";
import { showToast } from "@/components/ui/toast";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

const AUTH_PATH_PREFIXES = ["/login", "/register", "/forgot-password", "/reset-password"];

function isInAuthGroup(pathname: string): boolean {
  return AUTH_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/**
 * Context to expose sign out handlers to the rest of the app.
 */
interface SignOutContextValue {
  promptSignOut: () => void;
  promptQuickSignOut: () => void;
  promptClearData: () => void;
  promptRevokeSession: (sessionId: string) => void;
  isChecking: () => boolean;
  isSigningOut: () => boolean;
}

const SignOutContext = createContext<SignOutContextValue>();

export function useSignOut() {
  const context = useContext(SignOutContext);
  if (!context) {
    throw new Error("useSignOut must be used within SignOutProvider (BootManager)");
  }
  return context;
}

/**
 * BootManager coordinates the loading state of the entire application
 * using appBootMachine from @gymlog/shared.
 *
 * It acts as a gatekeeper that only renders the application content
 * once all parallel boot processes (DB, I18n, Fonts, Session) are ready.
 */
export const BootManager: ParentComponent = (props) => {
  const actorRef = useActorRef(appBootMachine);
  const state = fromActorRef(actorRef);

  const ps = usePowerSyncContext();
  const session = useSession();
  const executeSignOut = useExecuteSignOut();
  const location = useLocation();
  const navigate = useNavigate();

  // 1. Database Readiness
  createEffect(() => {
    if (!ps.isLoading() && ps.db()) {
      actorRef.send({ type: "DB_READY" });
    }
  });

  // 2. Resolve non-blocking web resources immediately on mount
  onMount(() => {
    actorRef.send({ type: "I18N_READY" });
    actorRef.send({ type: "FONTS_LOADED" });
    actorRef.send({ type: "TYPOGRAPHY_READY" });
    actorRef.send({ type: "THEME_READY" });
  });

  // 5. Session & Auth Group Detection
  createEffect(() => {
    const { isPending } = session;
    const hasSession = session.isAuthenticated();
    // Multi-account: `/login?mode=add` / `/register?mode=add` ADD a second
    // account while the current one stays signed in. Such a route must NOT count
    // as "in the auth group" — otherwise the boot machine sees an authenticated
    // user on an auth page and bounces them home, so the add-account form never
    // renders (the reported "can't add a 2nd account" bug). Treat it as in-app.
    const loc = location();
    const isAddingAccount = (loc.search as { mode?: string }).mode === "add";
    const inAuthGroup = !isAddingAccount && isInAuthGroup(loc.pathname);

    if (!isPending) {
      actorRef.send({
        type: "SESSION_RESOLVED",
        hasSession,
        inAuthGroup,
      });
    }
  });

  // 6. Centralized Redirection (authenticated user on an auth page → app).
  createEffect(() => {
    if (state().matches("redirecting") && untrack(() => session.isAuthenticated())) {
      void navigate({ to: "/" });
    }
  });

  const isReady = () => state().matches("ready") || state().matches("redirecting");

  return (
    <SignOutProvider
      db={ps.db()}
      session={session.data}
      disconnectAndClear={ps.disconnectAndClear}
      isReady={isReady()}
      onRedirectToSignIn={() => {
        executeSignOut();
      }}
    >
      {props.children}
    </SignOutProvider>
  );
};

function SignOutProvider(props: {
  db: AbstractPowerSyncDatabase | null;
  session: Session | null | undefined;
  disconnectAndClear: () => Promise<void>;
  isReady: boolean;
  onRedirectToSignIn: () => void;
  children: JSX.Element;
}) {
  const { t } = useI18n();

  const signOutInput = createMemo(() => ({
    db: props.db,
    session: props.session,
    // On web, useExecuteSignOut (triggered by onRedirectToSignIn) handles the
    // database cleanup and server sign-out in a non-blocking background task.
    // We pass no-ops here to prevent duplicate execution, database contention
    // and network/timeout races.
    disconnectAndClear: async () => {
      // no-op on web: handled by executeSignOut
    },
    onSignOut: async () => {
      // no-op on web: handled by executeSignOut
    },
    onRedirectToSignIn: props.onRedirectToSignIn,
    // P23/CC-60 · phase 3 — `.error` CANNOT be ignored. `api` is
    // openapi-fetch: **never throws**, so a 500 arrived here as
    // `{ error }`, the actor exited via `onDone` and the dialog closed as if
    // the session had been revoked. Measured: zero notices, row intact, and the
    // device REMAINS SIGNED IN. `fetchApi` (→ `handleApiResponse`) throws on
    // any non-2xx, which is the only thing that drives the machine to `onError`.
    onRevokeSession: async (sessionId: string) => {
      await fetchApi(api.POST("/api/auth/revoke-session", { body: { id: sessionId } }));
    },
    // Link 3: without this NOT EVEN A SUCCESS refreshes the list. Measured on
    // `main`: after revoking for real (device loses access on
    // server), row remained listed at 1/3/6/10/15 s without a single
    // `list-sessions` request emitted. Not an intermittent bounce of
    // `refetchOnWindowFocus`: it was NONEXISTENT.
    onRevokeSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.auth.activeSessions.all() });
    },
    onRevokeError: () => {
      showToast(t(msg`Could not revoke the session. Please try again.`), "error");
      // List is refreshed anyway: user needs to see the ACTUAL server
      // state, not what was rendered before the attempt.
      void queryClient.invalidateQueries({ queryKey: queryKeys.auth.activeSessions.all() });
    },
  }));

  const signOutActor = useActorRef(signOutMachine, {
    input: untrack(() => signOutInput()),
  });

  const signOutState = fromActorRef(signOutActor);
  const profileHandlers = createProfileHandlers(signOutActor.send);

  const isStandardSignOut = () => signOutState().matches("confirmSignOut");
  const isClearConfirm = () => signOutState().matches("confirmClear");
  const isRevokeConfirm = () => signOutState().matches("confirmRevoke");
  const isChecking = () => signOutState().matches("checkingUnsynced");
  const isSigningOut = () => isSignOutInProgress(signOutState());
  const hasUnsyncedChanges = () => signOutState().context.hasUnsyncedChanges;

  const confirmSignOut = () => {
    signOutActor.send({ type: "CONFIRMED" });
  };

  const cancelSignOut = () => {
    signOutActor.send({ type: "CANCELLED" });
  };

  return (
    <SignOutContext.Provider
      value={{
        promptSignOut: profileHandlers.promptSignOut,
        promptQuickSignOut: profileHandlers.promptQuickSignOut,
        promptClearData: profileHandlers.promptClearData,
        promptRevokeSession: profileHandlers.promptRevokeSession,
        isChecking,
        isSigningOut,
      }}
    >
      {props.children}

      <ConfirmDialog
        open={isStandardSignOut() || isSigningOut()}
        onOpenChange={(open) => {
          if (!open && !isSigningOut()) {
            cancelSignOut();
          }
        }}
        title={hasUnsyncedChanges() ? t(msg`Unsynced Changes`) : t(msg`Sign Out?`)}
        description={
          hasUnsyncedChanges()
            ? t(
                msg`You have changes that haven't been synced to the server yet. If you sign out now, these changes will be permanently lost.`,
              )
            : t(msg`Are you sure you want to sign out on this device?`)
        }
        confirmLabel={hasUnsyncedChanges() ? t(msg`Sign Out Anyway`) : t(msg`Sign Out`)}
        variant="destructive"
        onConfirm={confirmSignOut}
        isLoading={isSigningOut()}
      />

      <ConfirmDialog
        open={isClearConfirm() || signOutState().matches("clearing")}
        onOpenChange={(open) => {
          if (!open && !signOutState().matches("clearing")) {
            cancelSignOut();
          }
        }}
        title={t(msg`Clear local data`)}
        description={t(
          msg`This will delete all your local data. This action cannot be undone. Continue?`,
        )}
        confirmLabel={t(msg`Delete Everything`)}
        variant="destructive"
        onConfirm={confirmSignOut}
        isLoading={signOutState().matches("clearing")}
      />

      <ConfirmDialog
        open={isRevokeConfirm() || signOutState().matches("revoking")}
        onOpenChange={(open) => {
          if (!open && !signOutState().matches("revoking")) {
            cancelSignOut();
          }
        }}
        title={t(msg`Revoke Session`)}
        description={t(msg`This will sign out the device immediately. Continue?`)}
        confirmLabel={t(msg`Revoke`)}
        variant="destructive"
        onConfirm={confirmSignOut}
        isLoading={signOutState().matches("revoking")}
      />
    </SignOutContext.Provider>
  );
}
